import { useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import { useShopState } from "../state/shop-state";
import { post } from "../api";
import AddressForm from "../components/address-form";
import ExternalLink from "../components/external-link";

const names = {
    striker: "StanceBeam Striker",
    batgrip: "StanceBeam Bat Grip",
    tripod: "Tripod"
};

const Checkout = () => {
    const shippingFormRef = useRef();
    const billingFormRef = useRef();
    const [differentBillingAddress, setDifferentBillingAddress] = useState(false);
    const [paymentMode, setPaymentMode] = useState("card");
    const [captcha, setCaptcha] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { prices, quantity, setOrder } = useShopState();
    const orderSummary = useMemo(() => {
        const products = [];
        let total = 0;
        Object.keys(quantity).forEach((key) => {
            const subtotal = prices[key] * quantity[key];
            total += subtotal;
            if (subtotal > 0) {
                products.push({
                    id: key,
                    name: names[key],
                    quantity: quantity[key],
                    subtotal
                });
            }
        });
        return {
            products,
            total
        };
    }, [quantity, prices]);
    const placeOrder = async () => {
        const shippingAddress = shippingFormRef.current();
        let billingAddress = shippingAddress;
        if (differentBillingAddress) {
            billingAddress = billingFormRef.current();
        }
        if (!captcha) {
            setCaptcha("error");
        }
        const hasCaptcha = (typeof captcha === "string") && (captcha !== "error");
        if (shippingAddress && billingAddress && hasCaptcha) {
            setLoading(true);
            setOrder({
                shippingAddress,
                billingAddress,
                paymentMode,
                products: orderSummary.products,
                amount: orderSummary.total
            });
            try {
                const selectedProducts = orderSummary.products.map(({ id, quantity, subtotal }) => ({
                    id,
                    type: id === "striker" ? "product" : "accessory",
                    quantity,
                    amount: subtotal
                }));
                const data = await post({
                    identity: "promo.service.prepurchase",
                    amount: orderSummary.total,
                    discountPrice: 0,
                    quantity: 1,
                    userProfile: {
                        shippingAddress,
                        billingAddress
                    },
                    paymentMode,
                    selectedProducts
                });
                setLoading(false);
                console.log(data);
                if (data.status === "success") {
                    if (data.paymentMode === "card") {
                        window.location.href = data.longurl;
                        return;
                    }
                    return navigate(`/order?payment_status=COD&payment_request_id=${data.txnId}`, {
                        replace: true
                    });
                }
                setError("Something went wrong! Please try again.");
            } catch (error) {
                setError("Something went wrong! Please try again.");
                setLoading(false);
            }
        }
    };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto px-4 py-12">
            <div>
                <h2 className="text-xl lg:text-2xl font-bold">Shipping Address</h2>
                <AddressForm className="mt-4" submitRef={shippingFormRef} />
                <div className="flex items-center gap-4 mt-8">
                    <input
                        type="checkbox"
                        className="form-checkbox border-black text-brand focus:ring-brand"
                        id="different-billing-address"
                        onChange={({ target }) => setDifferentBillingAddress(target.checked)} />
                    <label htmlFor="different-billing-address">Use Different Billing Address</label>
                </div>
                {differentBillingAddress && (
                    <>
                        <h2 className="mt-8 text-xl lg:text-2xl font-bold">Billing Address</h2>
                        <AddressForm className="mt-4" submitRef={billingFormRef} />
                    </>
                )}
            </div>
            <div>
                <h2 className="text-xl lg:text-2xl font-bold">Order Summary</h2>
                <div className="flex items-center justify-between gap-4 h-10 mt-4 px-4 bg-grey-200 rounded">
                    <span>Product</span>
                    <span>Subtotal</span>
                </div>
                {orderSummary.products.map((product) => (
                    <div key={product.id} className="flex justify-between gap-4 mt-4 px-4">
                        <span>{product.name} X {product.quantity}</span>
                        <span>₹{product.subtotal}.0</span>
                    </div>
                ))}
                <div className="flex items-center justify-between gap-4 h-10 mt-4 px-4 bg-grey-200 rounded font-bold">
                    <span>Total</span>
                    <span>₹{orderSummary.total}.0</span>
                </div>
                <div className="mt-8">
                    <span>Payment Mode</span>
                    <div className="flex gap-4 mt-2">
                        <div className="radio-wrapper" onClick={() => setPaymentMode("card")}>
                            <div className="radio" data-active={paymentMode === "card"} />
                            <span>Pay Now</span>
                        </div>
                        {/* {quantity.striker > 0 && (
                            <div className="radio-wrapper" onClick={() => setPaymentMode("cod")}>
                                <div className="radio" data-active={paymentMode === "cod"} />
                                <span>Cash on Delivery</span>
                            </div>
                        )} */}
                    </div>
                    {paymentMode === "card" && (
                        <>
                            <div className="flex flex-wrap gap-4 mt-4 text-sm text-grey-600">
                                <div className="">
                                    <img src="/shop/static/images/upi-logo.webp" alt="UPI (Unified Payments Interface)" className="h-6" />
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 fill-grey-600">
                                        <path d="M0 0h24v24H0V0z" fill="none" />
                                        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                                    </svg>
                                    <span>Credit/Debit Card</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 fill-grey-600">
                                        <path d="M0 0h24v24H0z" fill="none" />
                                        <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
                                    </svg>
                                    <span>Wallets</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 fill-grey-600">
                                        <path d="M0 0h24v24H0z" fill="none" />
                                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
                                    </svg>
                                    <span>Net Banking</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" enableBackground="new 0 0 24 24" viewBox="0 0 24 24" className="w-6 h-6 fill-grey-600">
                                        <g><rect fill="none" height="24" width="24" /></g>
                                        <g><path d="M21,12V6c0-1.1-0.9-2-2-2h-1V2h-2v2H8V2H6v2H5C3.9,4,3,4.9,3,6v14c0,1.1,0.9,2,2,2h7v-2H5V10h14v2H21z M15.64,20 c0.43,1.45,1.77,2.5,3.36,2.5c1.93,0,3.5-1.57,3.5-3.5s-1.57-3.5-3.5-3.5c-0.95,0-1.82,0.38-2.45,1l1.45,0V18h-4v-4h1.5l0,1.43 C16.4,14.55,17.64,14,19,14c2.76,0,5,2.24,5,5s-2.24,5-5,5c-2.42,0-4.44-1.72-4.9-4L15.64,20z" /></g>
                                    </svg>
                                    <span>EMI</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" enableBackground="new 0 0 24 24" viewBox="0 0 24 24" className="w-6 h-6 fill-grey-600">
                                        <g>
                                            <rect fill="none" height="24" width="24" />
                                        </g>
                                        <g>
                                            <rect height="7" width="3" x="4" y="10" />
                                            <rect height="7" width="3" x="10.5" y="10" />
                                            <rect height="3" width="20" x="2" y="19" />
                                            <rect height="7" width="3" x="17" y="10" />
                                            <polygon points="12,1 2,6 2,8 22,8 22,6" />
                                        </g>
                                    </svg>
                                    <span>NEFT/Bank Transfer</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className="flex flex-col items-center mt-8">
                    <ReCAPTCHA
                        sitekey="6LdZ6ucfAAAAACStpO7WhSs3AoDNk1XLOyNs1rHP"
                        onChange={setCaptcha} />
                    {captcha === "error" && (
                        <p className="text-sm text-red-500 mt-1">Please verify that you are not a robot.</p>
                    )}
                </div>
                <div className="mt-8">
                    <button className="btn text brand shadow w-full transition-shadow active:shadow-none" onClick={placeOrder} disabled={loading}>
                        {loading ? (
                            <div className="spinner" />
                        ) : "Place Order"}
                    </button>
                    <p className="mt-2 text-sm">
                        By placing this order, I accept the <ExternalLink to="https://www.stancebeam.com/terms-of-use" className="font-bold underline text-blue-700" text="Terms of Use" /> and <ExternalLink to="https://www.stancebeam.com/return-policy" className="font-bold underline text-blue-700" text="Return Policy" />
                    </p>
                    {error && (
                        <p className="text-red-500 text-center mt-4">{error}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Checkout;
